import { supabase } from "../supabase/client";
import { ExpenseSection } from "../../lib/types";

export type ExpensesProfileSettings = {
  version: 1;
  appliedSearchQuery: string;
  paidFilter: "all" | "paid" | "unpaid";
  recurringFilter: "all" | "recurring" | "one-time";
  categoryFilter: string;
  vendorFilter: string;
  sharedOnly: boolean;
  groupingMode: "time" | "recurrence";
  expandedSection: ExpenseSection | null;
  allExpanded: boolean;
  pinnedExpenseIds: string[];
  includePendingDepositsInOverdue?: boolean;
  includeAvailableBalancesInOverdue?: boolean;
};

export type ExpensesProfile = {
  id: string;
  name: string;
  settings: ExpensesProfileSettings;
  createdAt: number;
};

type DbExpensesProfile = {
  id: string;
  name: string;
  settings: any;
  created_at: string;
};

export const EXPENSES_PROFILES_CHANGED_EVENT = "expensesProfiles:changed";

export const notifyExpensesProfilesChanged = () => {
  window.dispatchEvent(new Event(EXPENSES_PROFILES_CHANGED_EVENT));
};

export const onExpensesProfilesChanged = (handler: () => void) => {
  window.addEventListener(EXPENSES_PROFILES_CHANGED_EVENT, handler);
  return () =>
    window.removeEventListener(EXPENSES_PROFILES_CHANGED_EVENT, handler);
};

const mapDbToProfile = (row: DbExpensesProfile): ExpensesProfile => {
  return {
    id: row.id,
    name: row.name,
    settings: row.settings,
    createdAt: Number.isFinite(Date.parse(row.created_at))
      ? Date.parse(row.created_at)
      : Date.now(),
  };
};

export const expensesProfilesDB = {
  getAll: async (): Promise<ExpensesProfile[]> => {
    const { data, error } = await supabase
      .from("expenses_profiles")
      .select("id,name,settings,created_at")
      .order("created_at", { ascending: true });
    if (error) {
      console.error("[expensesProfilesDB] getAll failed", error);
      return [];
    }
    return (data || []).map(mapDbToProfile);
  },

  upsert: async (profile: ExpensesProfile): Promise<ExpensesProfile | null> => {
    const { data, error } = await supabase
      .from("expenses_profiles")
      .upsert(
        [{ id: profile.id, name: profile.name, settings: profile.settings }],
        { onConflict: "id" }
      )
      .select("id,name,settings,created_at")
      .single();
    if (error) {
      console.error("[expensesProfilesDB] upsert failed", error);
      return null;
    }
    notifyExpensesProfilesChanged();
    return mapDbToProfile(data as DbExpensesProfile);
  },

  delete: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from("expenses_profiles")
      .delete()
      .eq("id", id);
    if (error) {
      console.error("[expensesProfilesDB] delete failed", error);
      return false;
    }
    notifyExpensesProfilesChanged();
    return true;
  },
};
